import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { useNotificationStore } from '../stores/notificationStore';
import { lookupSaveError } from './lookupSaveError';

// ===== GENERIC REFERENCE DATA HOOKS =====

/** Query key factory for reference data */
export const refDataKeys = {
  all: ['refData'] as const,
  list: (table: string, facilityId: number) =>
    [...refDataKeys.all, table, 'list', facilityId] as const,
};

/**
 * A reference table changed: refresh EVERY cached copy of it, not only the Tables page's list.
 * Screens read the same table through other caches (lookupApi's ['lookup', 'allergyGroups', fac]
 * kept 10 minutes, ['allergyGroups', ...], resident and item pickers), so a group added in Tables
 * did not show in the Global Adjustment dialog until the program was closed (tester, 2026-10-27).
 * Any query whose key names the table is marked stale; the ones on screen refetch.
 */
export function invalidateReferenceTable(queryClient: QueryClient, table: string): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.includes(table),
  });
}

/**
 * Generic hook to fetch a list of reference data items.
 * @param table - unique table key (e.g., 'dietTypes')
 * @param facilityId - current facility ID
 * @param endpoint - API endpoint URL
 */
export function useRefDataList<T>(table: string, facilityId: number, endpoint: string) {
  return useQuery<T[]>({
    queryKey: refDataKeys.list(table, facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<T[]>(endpoint);
      return data;
    },
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Generic mutation hook to save (add or modify) a reference data item.
 * @param table - unique table key for cache invalidation
 * @param facilityId - current facility ID
 * @param endpoint - API endpoint URL for POST
 * @param entityLabel - display name for notifications (e.g., 'Diet Type')
 */
export function useRefDataSave<T>(
  table: string,
  facilityId: number,
  endpoint: string,
  entityLabel: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (item: Partial<T>) => {
      const { data } = await apiClient.post<number>(endpoint, item);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: refDataKeys.list(table, facilityId) });
      void invalidateReferenceTable(queryClient, table);
      useNotificationStore.getState().addNotification(`${entityLabel} saved`, 'success');
    },
    onError: (error: any) => {
      // The lookup endpoints answer { error, field }; String() of that was "[object Object]".
      const msg = lookupSaveError(error, `Failed to save ${entityLabel}`).message;
      useNotificationStore.getState().addNotification(msg, 'error');
    },
  });
}

/**
 * Generic mutation hook to delete a reference data item.
 * @param table - unique table key for cache invalidation
 * @param facilityId - current facility ID
 * @param endpointFn - function that takes an item ID and returns the DELETE endpoint URL
 * @param entityLabel - display name for notifications
 */
export function useRefDataDelete(
  table: string,
  facilityId: number,
  endpointFn: (id: number) => string,
  entityLabel: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(endpointFn(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: refDataKeys.list(table, facilityId) });
      void invalidateReferenceTable(queryClient, table);
      useNotificationStore.getState().addNotification(`${entityLabel} deleted`, 'success');
    },
    onError: (error: any) => {
      const msg = lookupSaveError(error, `Failed to delete ${entityLabel}`).message;
      useNotificationStore.getState().addNotification(msg, 'error');
    },
  });
}
